<lvr-nav-carousel [backLink]="backLink()" [carouselElements]="carouselElements()" [showCarousel]="false">
</lvr-nav-carousel>

<lvr-heading
	(editClicked)="openEdit()"
	[showEditButton]="
		!hideResetDetails() && isNotP4() && (canEditAssignment() || canEditAssignmentPhaseOneOnlyPermission())
	"
>
	{{ 'assignment.details.title' | transloco }}

	<div [matTooltip]="'assignment.details.send_mail_tooltip' | transloco" ngProjectAs="button-content">
		<lvr-button
			variant="primary"
			[label]="'base.button.send' | transloco"
			[ariaLabel]="'assignment.details.send_mail_tooltip' | transloco"
			[type]="'button'"
			(clicked)="openMail('')"
			icon="forward_to_inbox"
		></lvr-button>
	</div>
</lvr-heading>

@if (canShowReset() || resetStatus() === 'refreshing' || resetStatus() === 'error' || resetStatus() === 'unavailable') {
	<section lvrPadding [padHorizontal]="32" [padVertical]="16" aria-live="polite">
		@if (canShowReset()) {
			<lvr-button
				variant="primary"
				[label]="'assignment.reset_to_phase_one.action' | transloco"
				[type]="'button'"
				[disabled]="resetStatus() === 'posting' || resetStatus() === 'refreshing' || hideResetDetails()"
				(clicked)="confirmReset()"
			></lvr-button>
		}
		@if (resetStatus() === 'error' || resetStatus() === 'unavailable') {
			<p>
				{{
					(resetStatus() === 'error'
						? 'assignment.reset_to_phase_one.error'
						: 'assignment.reset_to_phase_one.unavailable'
					) | transloco
				}}
			</p>
		}
	</section>
}

@if (!hideResetDetails()) {
	@let entity = assignment();
	@if (entity) {
		<lvr-assignment-details
			[assignment]="entity"
			[clientId]="clientId()"
			[initiators]="initiators()"
			[hideHeading]="hideHeading()"
		></lvr-assignment-details>
	}

	@if (assignment()?.phaseId === PHASE3 || assignment()?.phaseId === PHASE4) {
		<h2 lvrPadding [padHorizontal]="32" [padVertical]="0">
			<span>{{ 'assignment.details.leks' | transloco }}</span>
		</h2>
		@if (canCreateLekAction()) {
			<lvr-button
				style="margin-bottom: 1rem"
				variant="primary"
				[label]="'assignment.details.lek' | transloco"
				[ariaLabel]="'assignment.details.add_lek' | transloco"
				[type]="'button'"
				[disabled]="!canCreateLekAction() || !shouldBeAbleToCreateLek()"
				(clicked)="openCreateLek()"
				icon="add"
			></lvr-button>
		}
		@if (lekPage().totalEntities === 0) {
			<p lvrPadding [padHorizontal]="16" [padVertical]="0">{{ 'assignment.details.no_leks' | transloco }}</p>
		} @else {
			<cdk-accordion class="assignment-accordion" [multi]="true">
				<lvr-assignment-accordion-item
					[assignmentId]="assignment()?.id ?? 0"
					[phaseId]="assignment()?.phaseId ?? 0"
					[disableTitleNavigation]="true"
					[disableTitleNavigationTooltip]="''"
					[lockOpen]="true"
					[expanded]="true"
					[hideHeader]="true"
				>
					<span ngProjectAs="accordion-title">{{ '' }}</span>
					<table class="lek-table">
						<tr>
							<th>{{ 'lek.list.name' | transloco }}</th>
							<th>{{ 'lek.list.start' | transloco }}</th>
							<th>{{ 'lek.list.end' | transloco }}</th>
							<th>{{ 'lek.list.duration' | transloco }}</th>
							<th>{{ 'lek.list.sponsor' | transloco }}</th>
						</tr>
						@for (lek of leks(); track lek.id) {
							<tr class="link" [routerLink]="['lek', lek.id]">
								<td>
									<span class="lek-name-link">
										<span class="lek-name-link__text">{{ lek.categoryAssistance.description }}</span>
										<span class="lek-name-link__arrow" aria-hidden="true"></span>
									</span>
								</td>
								<td>{{ lek.startDate ? (lek.startDate | date: 'dd.MM.yyyy') : '-' }}</td>
								<td>{{ lek.endDate ? (lek.endDate | date: 'dd.MM.yyyy') : '-' }}</td>
								<td>{{ lek.duration ? lek.duration : '-' }}</td>
								<td>
									@if (lekInstitutionId(lek); as institutionId) {
										<a
											class="btn-link sponsor-link"
											[routerLink]="['/desk', 'institution', institutionId]"
											(click)="$event.stopPropagation()"
											>{{ lekInstitutionName(lek) ?? '-' }}</a
										>
									} @else {
										-
									}
								</td>
							</tr>
						}
					</table>
					<lvr-paginator
						class="lek-paginator"
						[length]="lekPage().totalEntities"
						[pageSize]="lekPageSize()"
						[pageIndex]="lekPageIndex()"
						[showFirstLastButtons]="showFirstLastButtons"
						preferenceKey="assignment-view"
						(page)="lekPageChanged($event)"
					></lvr-paginator>
				</lvr-assignment-accordion-item>
			</cdk-accordion>
		}
	}

	<h2 lvrPadding [padHorizontal]="32" [padVertical]="0">
		<span style="margin-right: 8px">{{ 'assignment.details.reports' | transloco }}</span>
	</h2>
	@if (canCreateReports()) {
		<lvr-action-menu [items]="menuItems()"></lvr-action-menu>
	}
	@if (reports().length === 0) {
		<p lvrPadding [padHorizontal]="16" [padVertical]="0">{{ 'assignment.details.no_reports' | transloco }}</p>
	} @else {
		<div class="report-list">
			@for (report of reports(); track report.id) {
				<button
					class="report-row"
					[disabled]="!canReadReportsPermission()"
					(click)="routeBasedOnReportCategory(report)"
				>
					<span class="report-title-group">
						<b class="report-category">{{ report.categoryReport.description }}</b>
						<div>
							<span class="report-created">{{ 'assignment.details.report_created' | transloco }}:</span>
							<span class="report-date">
								{{ report.createdAt | date: 'dd.MM.yyyy' }}
							</span>
						</div>
					</span>
					<mat-chip class="chip-default">
						@if (report.isFinal) {
							{{ 'assignment.details.finished_report' | transloco }}
						} @else if (!report.isFinal && report.isDraft) {
							{{ 'assignment.details.draft_report' | transloco }}
						} @else if (!report.isFinal && !report.isDraft) {
							{{ 'assignment.details.ready_report' | transloco }}
						}
					</mat-chip>
				</button>
			}
		</div>
	}
}
